import React, { useEffect } from 'react';
import { 
  HelpCircle, Phone, Mail, MessageCircle, Clock, MapPin,
  FileText, Shield, Wrench, ArrowRight, ChevronRight
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const SoporteNosotrosPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactOptions = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Respuesta inmediata en horario laboral',
      action: 'Chatear ahora',
      link: 'https://wa.me/595991344956',
      available: '24/7'
    },
    {
      icon: Phone,
      title: 'Teléfono',
      description: 'Hablá directamente con un asesor',
      action: '+595 991 344956',
      link: 'tel:+595991344956',
      available: 'Lun-Vie 8:00-18:00'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'Para consultas detalladas',
      action: 'zenexparaguay@gmail.com',
      link: 'mailto:zenexparaguay@gmail.com',
      available: 'Respuesta en 24h'
    }
  ];

  const quickLinks = [
    {
      icon: HelpCircle,
      title: 'Preguntas Frecuentes',
      description: 'Respuestas a las dudas más comunes',
      link: '/soporte/faq'
    },
    {
      icon: Shield,
      title: 'Garantías',
      description: 'Información sobre cobertura y reclamos',
      link: '/soporte/garantias'
    },
    {
      icon: Wrench,
      title: 'Servicio Técnico',
      description: 'Mantenimiento y reparaciones',
      link: '/soporte/servicio-tecnico'
    },
    {
      icon: FileText,
      title: 'Documentación',
      description: 'Manuales y guías de usuario',
      link: '#'
    }
  ];

  const offices = [
    {
      city: 'Asunción',
      address: 'Facundo Machain 7390, casi Francis Morices. Asunción, Paraguay',
      phone: '+595 991 344956',
      hours: 'Lun-Vie 8:00-18:00, Sáb 8:00-12:00'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="soporte-nosotros-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-zinc-900 via-zinc-800 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Centro de Ayuda</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Soporte
            <span className="block text-emerald-400">Estamos para Ayudarte</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl">
            Nuestro equipo de soporte está disponible para resolver cualquier 
            duda o problema que tengas con tu sistema solar.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">
            ¿Cómo preferís contactarnos?
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {contactOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <a 
                  key={index}
                  href={option.link}
                  target={option.link.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all group text-center"
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-600 transition-colors">
                    <Icon className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-1">{option.title}</h3>
                  <p className="text-zinc-500 text-sm mb-3">{option.description}</p>
                  <p className="text-emerald-600 font-medium">{option.action}</p>
                  <p className="text-xs text-zinc-400 mt-2">{option.available}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">
            Recursos de Autoayuda
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {quickLinks.map((item, index) => {
              const Icon = item.icon;
              return (
                <Link 
                  key={index}
                  to={item.link}
                  className="flex items-center gap-4 p-6 bg-white rounded-xl hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-600 transition-colors">
                    <Icon className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-zinc-900 group-hover:text-emerald-600 transition-colors">{item.title}</h3>
                    <p className="text-sm text-zinc-500">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-emerald-600 transition-colors" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Office Location */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">
            Nuestra Oficina
          </h2>

          <div className="bg-zinc-50 rounded-xl p-8">
            {offices.map((office, index) => (
              <div key={index} className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-2">{office.city}</h3>
                  <div className="space-y-2 text-zinc-600">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      {office.address}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-600" />
                      {office.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-emerald-600" />
                      {office.hours}
                    </p>
                  </div>
                </div>
                <a 
                  href="https://maps.app.goo.gl/m9t2HJxER99saRix7"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('https://maps.app.goo.gl/m9t2HJxER99saRix7', '_blank');
                  }}
                  className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  Ver en Mapa
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency */}
      <section className="py-12 bg-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl font-bold text-zinc-900 mb-2">
            ¿Tenés una emergencia con tu sistema?
          </h2>
          <p className="text-zinc-600 mb-6">
            Si tu sistema dejó de funcionar o detectás algo inusual, contactanos inmediatamente.
          </p>
          <a 
            href="tel:+595991344956"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition-all"
          >
            <Phone className="w-5 h-5" />
            Línea de Emergencias: +595 991 344956
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SoporteNosotrosPage;
